import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import html2pdf from "html2pdf.js/dist/html2pdf.min.js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Athrv from "../../img/athrv-cloud-logo-1.png";
import AthrvDark from "../../img/Athrv-white.png";
import Mermaid from "./Mermaid";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthProvider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// TreeView Component for Directory
const TreeNode = ({ node, level = 0, onCheckboxChange, checkedItems, isDarkMode }) => {
  const [open, setOpen] = useState(false);
  const isFolder = node.type === "dir" || (node.children && node.children.length > 0);
  const nodePath = node.path || node.name;
  const isChecked = checkedItems[nodePath] || false;

  // Display "Select All" for root folder (level 0)
  const displayName = level === 0 ? "Select All" : (node.name || node.path);

  return (
    <>
      <ListItem sx={{ pl: level * 2 + 1 }}>
        <ListItemIcon sx={{ minWidth: 30 }}>
          <Checkbox
            checked={isChecked}
            onChange={(e) => onCheckboxChange(nodePath, e.target.checked, node)}
            size="small"
          />
        </ListItemIcon>
        {isFolder && (
          <ListItemIcon
            sx={{ minWidth: 30, cursor: 'pointer' }}
            onClick={() => setOpen(!open)}
          >
            {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </ListItemIcon>
        )}
        <ListItemIcon sx={{ minWidth: 30 }}>
          {isFolder ? (
            <FolderIcon sx={{ color: "#FFA500", fontSize: 20 }} />
          ) : (
            <InsertDriveFileIcon sx={{ color: "#666", fontSize: 20 }} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={displayName}
          primaryTypographyProps={{ 
            fontSize: '0.9rem',
            color: isDarkMode ? 'inherit' : '#000000'
          }}
        />
      </ListItem>
      {isFolder && node.children && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child, index) => (
              <TreeNode
                key={index}
                node={child}
                level={level + 1}
                onCheckboxChange={onCheckboxChange}
                checkedItems={checkedItems}
                isDarkMode={isDarkMode}  // Add this line
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

// Highlight keywords in code blocks
const highlightCode = (code, isDarkMode) => {
  const keywords = [
    "const",
    "let",
    "var",
    "return",
    "if",
    "else",
    "for",
    "while",
    "void",
    "int",
    "float",
    "double",
    "bool",
    "std",
    "function",
    "import",
    "export",
    "class",
    "new",
    "await",
    "async",
    "private",
    "public",
    "protected",
    "struct",
  ];

  const pattern = "\\b(" + keywords.join("|") + ")\\b";
  const keywordRegex = new RegExp(pattern, "g");

  const html = code
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(keywordRegex, '<span style="color: green;">$1</span>');

  const backgroundColor = isDarkMode ? "#2e2e2e" : "#f5f5f5";
  return `<pre style="background-color: ${backgroundColor}; padding: 10px;overflow-wrap: anywhere;">${html}</pre>`;
};

const AiAnalysis = ({ analysisResult }) => {
  const { projectId } = useParams();
  const { authToken, organizationId } = useAuth();
  const navigate = useNavigate();
  const [isFetching, setIsFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const location = useLocation();
  const activeTab = location.state?.activeTab;
  const [analysisStatus, setAnalysisStatus] = useState("invoked");
  const [statusIcon, setStatusIcon] = useState(null);
  const [report, setReport] = useState("");
  const analysisRef = useRef();
  const isPollingCancelled = useRef(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [isPolling, setIsPolling] = useState(false);

  // GitHub states
  const [openDialog, setOpenDialog] = useState(false);
  const [directoryData, setDirectoryData] = useState(null);
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [tempCheckedItems, setTempCheckedItems] = useState({});

  const CheckmarkIcon = () => (
    <svg
      className="checkmark"
      width="17"
      height="13"
      viewBox="0 0 17 13"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polyline
        points="1 7 5 11 15 1"
        stroke="#4caf50"
        strokeWidth="2"
        fill="none"
        strokeDasharray="20"
        strokeDashoffset="20"
      >
        <animate
          attributeName="stroke-dashoffset"
          from="20"
          to="0"
          dur="0.5s"
          begin="0.2s"
          fill="freeze"
        />
      </polyline>
    </svg>
  );

  const WaveLoadingIcon = ({ color = "#ffffff" }) => (
    <div className="icon">
      <svg
        version="1.1"
        id="L7"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        x="0px"
        y="0px"
        viewBox="0 0 100 100"
        xmlSpace="preserve"
      >
        <path
          fill={color}
          d="M31.6,3.5C5.9,13.6-6.6,42.7,3.5,68.4c10.1,25.7,39.2,38.3,64.9,28.1l-3.1-7.9c-21.3,8.4-45.4-2-53.8-23.3
      c-8.4-21.3,2-45.4,23.3-53.8L31.6,3.5z"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            dur="2s"
            from="0 50 50"
            to="360 50 50"
            repeatCount="indefinite"
          />
        </path>
        <path
          fill={color}
          d="M42.3,39.6c5.7-4.3,13.9-3.1,18.1,2.7c4.3,5.7,3.1,13.9-2.7,18.1l4.1,5.5c8.8-6.5,10.6-19,4.1-27.7
      c-6.5-8.8-19-10.6-27.7-4.1L42.3,39.6z"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            dur="1s"
            from="0 50 50"
            to="-360 50 50"
            repeatCount="indefinite"
          />
        </path>
        <path
          fill={color}
          d="M82,35.7C74.1,18,53.4,10.1,35.7,18S10.1,46.6,18,64.3l7.6-3.4c-6-13.5,0-29.3,13.5-35.3s29.3,0,35.3,13.5
      L82,35.7z"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            dur="2s"
            from="0 50 50"
            to="360 50 50"
            repeatCount="indefinite"
          />
        </path>
      </svg>
    </div>
  );

  // Helper function to get all child paths from a node (for checkbox state management)
  const getAllChildPaths = (node) => {
    let paths = [];
    const nodePath = node.path || node.name;
    paths.push(nodePath);

    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        paths = paths.concat(getAllChildPaths(child));
      });
    }

    return paths;
  };

  // NEW FUNCTION: Extract only file paths (no folders) from checked items
  const getOnlyFilePaths = (checkedItemsObj) => {
    const filePaths = [];

    const extractFiles = (node) => {
      const nodePath = node.path || node.name;
      const isFolder = node.type === "dir" || (node.children && node.children.length > 0);

      // Only add if it's checked AND it's a file (not a folder)
      if (checkedItemsObj[nodePath] && !isFolder) {
        filePaths.push(nodePath);
      }

      // Recursively check children
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => extractFiles(child));
      }
    };

    if (directoryData) {
      extractFiles(directoryData);
    }

    return filePaths;
  };

  // Helper function to count selected folders and files
  const getSelectionCounts = () => {
    if (!directoryData) {
      return { folders: 0, files: 0 };
    }

    let folders = 0;
    let files = 0;

    const selectedPaths = Object.keys(checkedItems).filter(key => checkedItems[key]);

    const countInNode = (node, level = 0) => {
      const nodePath = node.path || node.name;

      if (selectedPaths.includes(nodePath)) {
        const isFolder = node.type === "dir" || (node.children && node.children.length > 0);
        if (isFolder) {
          // Skip root folder (level 0) from count
          if (level > 0) {
            folders++;
          }
        } else {
          files++;
        }
      }

      if (node.children && node.children.length > 0) {
        node.children.forEach(child => countInNode(child, level + 1));
      }
    };

    countInNode(directoryData, 0);

    return { folders, files };
  };

  const selectionCounts = useMemo(() => getSelectionCounts(), [checkedItems, directoryData]);

  const getSelectionCountsFromItems = (items) => {
    if (!directoryData || !items) {
      return { folders: 0, files: 0 };
    }

    let folders = 0;
    let files = 0;
    const selectedPaths = Object.keys(items).filter(key => items[key]);

    const countInNode = (node, level = 0) => {
      const nodePath = node.path || node.name;

      if (selectedPaths.includes(nodePath)) {
        const isFolder = node.type === "dir" || (node.children && node.children.length > 0);
        if (isFolder) {
          // Skip root folder (level 0) from count
          if (level > 0) {
            folders++;
          }
        } else {
          files++;
        }
      }

      if (node.children && node.children.length > 0) {
        node.children.forEach(child => countInNode(child, level + 1));
      }
    };

    countInNode(directoryData, 0);
    return { folders, files };
  };

  const handleOpenDialog = async () => {
    setOpenDialog(true);
    setTempCheckedItems({ ...checkedItems });

    if (directoryData) return;

    setLoadingDirectory(true);
    const token = localStorage.getItem("access_token");

    try {
      const baseUrl = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/github/folder-structure`;
      const apiUrl = `${baseUrl}?project_id=${projectId}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        setDirectoryData(null);
        return;
      }

      const data = await response.json();

      if (data.structure && data.structure.children) {
        setDirectoryData(data.structure);
      } else if (data.children) {
        setDirectoryData(data);
      } else {
        setDirectoryData(null);
      }
    } catch (err) {
      console.error("Directory fetch error:", err);
      setDirectoryData(null);
    } finally {
      setLoadingDirectory(false);
    }
  };

  const handleCloseDialog = () => {
    setCheckedItems({ ...tempCheckedItems });
    setOpenDialog(false);
  };

  const handleCheckboxChange = (path, checked, node) => {
    setCheckedItems(prev => {
      const newCheckedItems = { ...prev };

      const allPaths = node ? getAllChildPaths(node) : [path];

      allPaths.forEach(p => {
        newCheckedItems[p] = checked;
      });

      return newCheckedItems;
    });
  };

  const handleConfirmSelection = () => {
    const selectedFiles = Object.keys(checkedItems).filter(key => checkedItems[key]);

    if (selectedFiles.length === 0) {
      toast.warning("No items selected");
      setOpenDialog(false);
      return;
    }

    setTempCheckedItems({ ...checkedItems });

    const counts = getSelectionCountsFromItems(checkedItems);
    toast.success(`Selected ${counts.folders} folder(s) and ${counts.files} file(s)`);

    setOpenDialog(false);
  };

  const pollAnalysisStatus = () => {
  const token = localStorage.getItem("access_token");

  // DON'T set isPolling here anymore - it's already set in handleCurrentAIAnalysis
  // setIsPolling(true); // REMOVE THIS LINE

  const centeredWrapper = (icon) => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "150px",
        width: "100%",
      }}
    >
      {icon}
    </div>
  );

  const intervalId = setInterval(async () => {
    if (isPollingCancelled.current) {
      setIsPolling(false);
      clearInterval(intervalId);
      return;
    }

    try {
      const requestBody = { project_id: projectId };
      const statusResponse = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/ai-agent/pre-analysis-status-v2`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (isPollingCancelled.current) {
        setIsPolling(false);
        clearInterval(intervalId);
        return;
      }

      if (!statusResponse.ok) {
        throw new Error(`Status check failed: ${statusResponse.status}`);
      }

      const statusData = await statusResponse.json();
      const processStatus = statusData?.pre_ai_analysis_process;
      const analysisStatus = statusData?.pre_ai_analysis_status;
      const resultUrl = statusData?.latest_ai_analysis_report;

      console.log("Polling status:", { processStatus, analysisStatus });

      setAnalysisStatus(processStatus);

      if (processStatus === "invoked" || analysisStatus === "initialized") {
        setStatusIcon(centeredWrapper(<WaveLoadingIcon color="#FFEB3B" />));
      } else if (analysisStatus === "in-progress") {
        setStatusIcon(centeredWrapper(<WaveLoadingIcon color="#2196F3" />));
      } else if (processStatus === "completed") {
        setStatusIcon(centeredWrapper(<CheckmarkIcon />));

        if (resultUrl) {
          const resultResponse = await fetch(resultUrl);
          const resultText = await resultResponse.text();
          setAiResult(resultText);
          toast.success("AI Analysis completed successfully!");
        }

        setIsPolling(false);
        clearInterval(intervalId);
      } else if (processStatus === "failed") {
        toast.error("AI Analysis failed. Please try again.");
        setIsPolling(false);
        clearInterval(intervalId);
      }
    } catch (err) {
      console.error("Polling error:", err);
      toast.error("Error checking analysis status");
      setIsPolling(false);
      clearInterval(intervalId);
    }
  }, 8000);

  return intervalId;
};

  useEffect(() => {
    let intervalId;

    if (isPolling) {
      console.log("🔄 Starting polling...");
      isPollingCancelled.current = false;
      intervalId = pollAnalysisStatus();
    } else {
      console.log("⏸️ Polling stopped");
    }

    return () => {
      console.log("🧹 Cleanup: Stopping polling");
      isPollingCancelled.current = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [isPolling]);

  // Auto-open file selector when navigated from ProjectBrief
  useEffect(() => {
    if (location.state?.openFileSelector) {
      handleOpenDialog();
      // Clear the state to prevent reopening on re-renders
      navigate(location.pathname, { 
        state: { ...location.state, openFileSelector: false },
        replace: true 
      });
    }
  }, [location.state?.openFileSelector]);

  // UPDATED: handleCurrentAIAnalysis - Now sends only file paths to API
  const handleCurrentAIAnalysis = async () => {
  // Validate that files are selected before proceeding
  const selectedFilePaths = getOnlyFilePaths(checkedItems);

  if (selectedFilePaths.length === 0) {
    toast.warning("Please select files first before running AI Analysis");
    return;
  }

  setIsSubmitting(true);
  setIsFetching(true); 
  
  const token = localStorage.getItem("access_token");

  try {
    console.log("Selected files for API:", selectedFilePaths);

    const requestBody = {
      project_id: projectId,
      organization_id: organizationId,
      jwt_access_token: token,
      service_mode: "ai-pre-analysis",
      selected_files: selectedFilePaths,
    };

    console.log("Request body:", requestBody);

    const apiUrl = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/ai-agent/pre-analysis-v2`;
    const analysisResponse = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!analysisResponse.ok) {
      setIsPolling(false);
      const errorData = await analysisResponse.json();
      const cleanMessage = errorData.message 
    ? errorData.message.replace(/:\s*Bad Request$/i, '').trim()
    : "Failed to start analysis";
      throw new Error(cleanMessage);
    }

    const analysisData = await analysisResponse.json();

    if (analysisData.notification_message) {
      toast.info(analysisData.notification_message);
    } else {
      toast.success(`AI Analysis started for ${selectedFilePaths.length} selected file(s)`);
    }

    // Start polling by setting isPolling to true
    // The useEffect will handle starting the interval
    setIsPolling(true);
    
  } catch (err) {
    console.error("Analysis error:", err);
    toast.error(err.message || "Failed to analyze the project.");
    setIsPolling(false);
  } finally {
    setIsFetching(false);
    setIsSubmitting(false);
    setAnalysisStatus("invoked");
  }
};

  const handleDownloadPDF = () => {
    setLoading(true);

    const original = analysisRef.current;
    if (!original) {
      setLoading(false);
      return;
    }

    const clone = original.cloneNode(true);
    clone.classList.add("force-light-mode");
    clone.style.backgroundColor = "white";
    clone.style.color = "black";
    clone.style.fontFamily = "Arial, sans-serif";
    clone.style.wordBreak = "break-word";
    clone.style.overflowWrap = "anywhere";
    clone.style.padding = "20px";

    const logoImg = clone.querySelector('img[alt="Logo"]');
    if (logoImg) logoImg.src = Athrv;

    const tables = clone.querySelectorAll("table");
    tables.forEach((table) => {
      table.style.borderCollapse = "collapse";
      table.style.width = "100%";
      table.style.tableLayout = "auto";
    });

    const tableHeaders = clone.querySelectorAll("th");
    tableHeaders.forEach((th) => {
      th.style.border = "1px solid #999";
      th.style.backgroundColor = "#f0f0f0";
      th.style.color = "black";
      th.style.padding = "6px";
      th.style.textAlign = "center";
      th.style.verticalAlign = "middle";
      th.style.whiteSpace = "normal";
      th.style.transform = "none";
      th.style.writingMode = "horizontal-tb";
      th.style.rotate = "none";
    });

    const tableCells = clone.querySelectorAll("td");
    tableCells.forEach((td) => {
      td.style.border = "1px solid #999";
      td.style.padding = "6px";
      td.style.textAlign = "left";
      td.style.verticalAlign = "top";
      td.style.whiteSpace = "normal";
      td.style.transform = "none";
      td.style.writingMode = "horizontal-tb";
      td.style.rotate = "none";
    });

    const codeBlocks = clone.querySelectorAll(
      "pre, code, td pre, td code, .hljs, .code-block"
    );
    codeBlocks.forEach((el) => {
      el.style.whiteSpace = "pre-wrap";
      el.style.wordBreak = "break-word";
      el.style.overflow = "auto";
      el.style.maxWidth = "100%";
      el.style.fontSize = "11px";
      el.style.backgroundColor = "white";
      el.style.color = "black";
      el.style.border = "1px solid #ccc";
      el.style.fontFamily = "Arial, sans-serif";
      el.style.padding = "6px";
      el.style.borderRadius = "4px";
    });

    const style = document.createElement("style");
    style.innerHTML = `
      pre, code, .hljs, .code-block {
        background-color: white !important;
        color: black !important;
        border: 1px solid #ccc;
        font-family: Arial, sans-serif;
        padding: 6px;
        border-radius: 4px;
        white-space: pre-wrap;
        word-break: break-word;
      }
    `;
    clone.appendChild(style);

    const wrapper = document.createElement("div");
    wrapper.style.position = "fixed";
    wrapper.style.top = "0";
    wrapper.style.left = "-9999px";
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    setTimeout(() => {
      html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: "ai-analysis.pdf",
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            logging: false,
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
          },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        })
        .from(clone)
        .save()
        .then(() => {
          document.body.removeChild(wrapper);
          setLoading(false);
        })
        .catch(() => {
          document.body.removeChild(wrapper);
          setLoading(false);
        });
    }, 100);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>

            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleCurrentAIAnalysis}
                  disabled={isFetching}
                  sx={{
                    backgroundColor: isDarkMode ? "#0066CC" : "#0C1A32",
                    "&:hover": { backgroundColor: "#002244" },
                    minWidth: 200,
                    height: 40,
                  }}
                >
                  {isFetching ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "AI Analysis"
                  )}
                </Button>

                <Tooltip
                  title={
                    <Box sx={{ p: 2, maxWidth: 400 }}>
                      <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
                        What's happening here?
                      </Typography>

                      <Typography variant="body2">
                        Our AI conducted a comprehensive code analysis examining your
                        code's architecture, security vulnerabilities, performance
                        issues, MISRA compliance, build system, and overall quality.
                      </Typography>

                      <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                        The report includes:
                      </Typography>

                      <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 1 }}>
                        <li><strong>Executive Summary</strong> – key findings</li>
                        <li><strong>Report Card</strong> – letter grades</li>
                        <li><strong>Architecture Analysis</strong> – structure review</li>
                        <li><strong>Security & Bug Detection</strong> – vulnerabilities</li>
                        <li><strong>MISRA Compliance</strong> – safety standards</li>
                        <li><strong>Performance Assessment</strong> – efficiency</li>
                        <li><strong>Build System Review</strong> – compilation & deps</li>
                      </Box>

                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Grade Scale:
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                        <li><strong>A:</strong> Excellent</li>
                        <li><strong>B:</strong> Good</li>
                        <li><strong>C:</strong> Average</li>
                        <li><strong>D:</strong> Below Average</li>
                        <li><strong>F:</strong> Failing</li>
                      </Box>
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <InfoOutlinedIcon sx={{ color: "#1976d2", cursor: "pointer" }} />
                </Tooltip>
              </Box>

              <Box sx={{ flexGrow: 1 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {(aiResult || analysisResult) && (
                  <Button
                    variant="outlined"
                    onClick={handleDownloadPDF}
                    disabled={loading}
                    sx={{
                      borderColor: isDarkMode ? "#0066CC" : "#0C1A32",
                      color: isDarkMode ? "#ffffff" : "#0C1A32",
                      backgroundColor: isDarkMode ? "transparent" : "inherit",
                      "&:hover": {
                        borderColor: "#002244",
                        backgroundColor: isDarkMode ? "rgba(0, 102, 204, 0.1)" : "#f0f0f0",
                      },
                      minWidth: 160,
                      height: 40,
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} sx={{ color: isDarkMode ? "#ffffff" : "#0C1A32" }} />
                    ) : (
                      "DOWNLOAD PDF"
                    )}
                  </Button>
                )}

                <Button
                  variant="contained"
                  onClick={handleOpenDialog}
                  sx={{
                    backgroundColor: isDarkMode ? "#0066CC" : "#0C1A32",
                    "&:hover": { backgroundColor: "#002244" },
                    minWidth: 160,
                    height: 40,
                  }}
                >
                  SELECT FILES
                </Button>

                <Button
                  variant="contained"
                  className="athrv-dash-primary-button"
                  sx={{ minWidth: 160, height: 40 }}
                  onClick={() => navigate(`/projects`)}
                >
                  BACK TO PROJECT LIST
                </Button>
              </Box>

              {/* Dialog Popup */}
              <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Select Files</DialogTitle>
                <DialogContent dividers sx={{ minHeight: 400, maxHeight: 500 }}>
                  {loadingDirectory ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : directoryData ? (
                   <List component="nav" disablePadding>
                    <TreeNode
                      node={directoryData}
                      onCheckboxChange={handleCheckboxChange}
                      checkedItems={checkedItems}
                      isDarkMode={isDarkMode}  // Add this line
                    />
                  </List>
                  ) : (
                    <Typography color="textSecondary">No directory data available</Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseDialog} color="inherit">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmSelection}
                    variant="contained"
                    sx={{
                      backgroundColor: isDarkMode ? "#0066CC" : "#0C1A32",
                      "&:hover": { backgroundColor: "#002244" },
                    }}
                  >
                    Confirm Selection
                  </Button>
                </DialogActions>
              </Dialog>

            </Box>

            {analysisStatus && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
                >
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6">
            AI Analysis Result
          </Typography>

          {(selectionCounts.folders > 0 || selectionCounts.files > 0) && (
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "0.875rem"
            }}>
              <FolderIcon sx={{ fontSize: 18, color: "#666" }} />
              <Typography variant="body2" sx={{ color: "#666" }}>
                {selectionCounts.folders} Folder{selectionCounts.folders !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mx: 0.5 }}>
                |
              </Typography>
              <InsertDriveFileIcon sx={{ fontSize: 18, color: "#666" }} />
              <Typography variant="body2" sx={{ color: "#666" }}>
                {selectionCounts.files} File{selectionCounts.files !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
  ref={analysisRef}
  className="analysis-container"
  sx={{
    mt: 2,
    border: "1px solid #ccc",
    p: 2,
    borderRadius: "8px",
    overflowX: "auto",
    position: "relative",
    wordBreak: "break-word",
    minHeight: "400px",
    overflow: "hidden", 
  }}
>
  {/* Show loading when polling, otherwise show content */}
  {isPolling ? (
    // Loading state - blank screen with spinner
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px",
        width: "100%",
      }}
    >
      <CircularProgress size={60} sx={{ color: isDarkMode ? "#0066CC" : "#0C1A32" }} />
      <Typography
        variant="h6"
        sx={{ 
          mt: 3, 
          color: isDarkMode ? "#ffffff" : "#0C1A32", 
          fontWeight: "bold" 
        }}
      >
        Fetching Latest Analysis...
      </Typography>
    </Box>
  ) : (
    // Normal content display
    <>
      <Box
        component="img"
        src={theme.palette.mode === "dark" ? AthrvDark : Athrv}
        alt="Logo"
        crossOrigin="anonymous"
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          width: 150,
          height: "auto",
          zIndex: 10,
        }}
      />

      {(aiResult || analysisResult) && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: (props) => (
              <Typography fontWeight="bold" gutterBottom fontSize="1.3rem" {...props} />
            ),
            h2: (props) => (
              <Typography fontWeight="bold" gutterBottom fontSize="1.2rem" {...props} />
            ),
            h3: (props) => (
              <Typography fontWeight="bold" gutterBottom fontSize="1.1rem" {...props} />
            ),
            h4: (props) => (
              <Typography fontWeight="bold" gutterBottom fontSize="0.9rem" {...props} />
            ),
            h5: (props) => (
              <Typography fontWeight="bold" gutterBottom fontSize="0.8rem" {...props} />
            ),
            h6: (props) => (
              <Typography fontWeight="bold" gutterBottom fontSize="0.7rem" {...props} />
            ),
            p: (props) => <Typography variant="body1" gutterBottom {...props} />,
            ul: (props) => <ul style={{ paddingLeft: "20px", marginBottom: "16px" }} {...props} />,
            ol: (props) => <ol style={{ paddingLeft: "20px", marginBottom: "16px" }} {...props} />,
            li: (props) => <li style={{ marginBottom: "4px" }} {...props} />,
            table: (props) => (
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  my: 2,
                  fontSize: "14px",
                  overflowWrap: "anywhere",
                }}
                {...props}
              />
            ),
            th: (props) => (
              <Box
                component="th"
                sx={{
                  border: "1px solid #999",
                  padding: "8px",
                  backgroundColor: isDarkMode ? "#585858" : "#f5f5f5",
                  color: isDarkMode ? "#ffffff" : "inherit",
                  textAlign: "left",
                }}
                {...props}
              />
            ),
            td: (props) => (
              <Box
                component="td"
                sx={{
                  border: "1px solid #ccc",
                  padding: "8px",
                  textAlign: "left",
                }}
                {...props}
              />
            ),
            code({ inline, children, className, ...props }) {
              const codeString = String(children).trim();
              const language = className?.replace("language-", "");

              if (!inline && language === "mermaid") {
                return <Mermaid chart={codeString} />;
              }

              if (inline) {
                return (
                  <code
                    style={{
                      backgroundColor: isDarkMode ? "#585858" : "#f5f5f5",
                      color: isDarkMode ? "#9fef9f" : "green",
                      padding: "2px 4px",
                      overflowWrap: "anywhere",
                    }}
                    {...props}
                  >
                    {codeString}
                  </code>
                );
              }

              return (
                <Box
                  component="pre"
                  className="code-block"
                  sx={{
                    mt: 2,
                    backgroundColor: isDarkMode ? "#383838" : "#f5f5f5",
                    color: isDarkMode ? "#ffffff" : "#000000",
                    p: 2,
                    overflowX: "auto",
                    "& code": {
                      backgroundColor: "transparent !important",
                      color: "inherit !important",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: highlightCode(codeString, isDarkMode),
                  }}
                />
              );
            },
          }}
        >
          {aiResult || analysisResult}
        </ReactMarkdown>
      )}
    </>
  )}
</Box>
      </Paper>
    </Box>
  );
};

export default AiAnalysis;